import type React from 'react'
import { ArrowUp, Globe, Paperclip, ScanText, Square } from 'lucide-react'
import { useEffect, useRef, forwardRef, useImperativeHandle, useState } from 'react'

import { nanoid } from 'nanoid'
import { AgentAttachmentType, type AgentAttachment } from '@multiplayer-app/ai-agent-types'
import { useAgentStore, useAgentRuntime } from '@/context/AgentProvider'

import { UiChatStatus } from '@/state/useAgentStore'
import { getChatState } from '@/state/useAgentStore'

import { Tooltip } from '@/components/ui/Tooltip'
import { ComposerAttachments } from '@/components/Attachments'

import { useTranslations } from '@/hooks/useTranslations'

import { normalizeUrl, getUrlName } from '@/utils/normalizeUrl'
import { subscribeToLocationChanges } from '@/utils/locationChange'
import { processFiles, type FileUploadProgress } from '@/utils/fileUpload'
import { validateFiles, getDefaultFileValidationConfig } from '@/utils/fileValidation'
import { captureTextSelectionTarget, createContextAttachment } from '@/utils/contextAttachments'

import { ModelSelector } from '../ModelSelector'
import { ToolConfigDropdown } from '../ToolConfigDropdown'

import type { ComposerFormApi } from '../types'

export interface ComposerInputRowProps {
  autoFocus?: boolean
  activeChatId?: string
  status: UiChatStatus
  onStop: () => void
  onSubmit: (value: string) => void
}
export type ComposerFormRowRef = ComposerFormApi

export const ComposerInputRow = forwardRef<ComposerFormRowRef, ComposerInputRowProps>(
  ({ autoFocus, activeChatId, status, onStop, onSubmit }, ref) => {
    const t = useTranslations()
    const runtime = useAgentRuntime()

    const textareaRef = useRef<HTMLTextAreaElement>(null)
    const fileInputRef = useRef<HTMLInputElement>(null)
    const chatKey = useAgentStore((s) => s.activeChatId ?? s.draftChatKey)
    const draft = useAgentStore((s) => getChatState(s, chatKey).composerDraft)
    const composerError = useAgentStore((s) => getChatState(s, chatKey).composerError)
    const pageContextDismissed = useAgentStore((s) => getChatState(s, chatKey).pageContextDismissed)
    const setComposerValue = useAgentStore((s) => s.setComposerValue)
    const setComposerDraft = useAgentStore((s) => s.setComposerDraft)
    const clearComposerDraft = useAgentStore((s) => s.clearComposerDraft)
    const setComposerError = useAgentStore((s) => s.setComposerError)
    const addComposerAttachments = useAgentStore((s) => s.addComposerAttachments)
    const removeComposerAttachment = useAgentStore((s) => s.removeComposerAttachment)
    const setPageContextDismissed = useAgentStore((s) => s.setPageContextDismissed)

    const localValue = draft.value
    const attachments = draft.attachments
    const [uploadProgress, setUploadProgress] = useState<Map<string, FileUploadProgress>>(new Map())
    const [uploadErrors, setUploadErrors] = useState<Map<string, string>>(new Map())

    const valueRef = useRef(localValue)
    const attachmentsRef = useRef(attachments)
    const dismissedRef = useRef(pageContextDismissed)

    const pageContextEnabled = runtime.config.features?.pageContextAttachments !== false
    const ComposerAttachmentActions = runtime.config.composerAttachmentActions
    const pageContextName = runtime.config.pageContext?.name?.trim() || 'Page context'
    const attachPageContextLabel = `Attach ${pageContextName}`
    const pageContextIcon = runtime.config.contextAttachmentIcons?.pageContext ?? <Globe size={16} />

    const isPageContext = (a: AgentAttachment) =>
      a.type === AgentAttachmentType.Context && (a as any)?.metadata?.kind === 'pageContext'
    const hasPageContext = attachments.some(isPageContext)

    const buildPageContextAttachment = (): AgentAttachment | undefined => {
      if (!pageContextEnabled) return
      return runtime.buildPageContextAttachment()
    }

    useEffect(() => {
      attachmentsRef.current = attachments
    }, [attachments])

    useEffect(() => {
      valueRef.current = localValue
    }, [localValue])

    useEffect(() => {
      dismissedRef.current = pageContextDismissed
    }, [pageContextDismissed])

    useEffect(() => {
      if (!pageContextEnabled) return
      if (pageContextDismissed) return
      if (hasPageContext) return
      const page = buildPageContextAttachment()
      if (page) {
        addComposerAttachments(chatKey, [page])
      }
    }, [pageContextEnabled, pageContextDismissed, hasPageContext, chatKey, addComposerAttachments])

    useEffect(() => {
      if (!pageContextEnabled) return
      if (typeof window === 'undefined') return
      return subscribeToLocationChanges(() => {
        if (dismissedRef.current) return
        const current = attachmentsRef.current
        const existing = current.find(isPageContext)
        if (!existing) return
        const next = buildPageContextAttachment()
        if (!next) return
        const updated: AgentAttachment = { ...next, id: existing.id }
        const nextAttachments = current.map((a) => (a.id === existing.id ? updated : a))
        setComposerDraft(chatKey, { value: valueRef.current, attachments: nextAttachments })
      })
    }, [pageContextEnabled, chatKey, setComposerDraft])

    useImperativeHandle(ref, () => {
      return {
        setValue: (value: string) => {
          setComposerValue(chatKey, value)
        },
        getDraft: () => ({ value: localValue, attachments }),
        setDraft: (draft) => {
          setComposerDraft(chatKey, { value: draft.value, attachments: draft.attachments ?? [] })
        },
        clearDraft: () => {
          clearComposerDraft(chatKey)
        },
        buildPageContextAttachment: () => {
          return buildPageContextAttachment()
        }
      }
    })

    useEffect(() => {
      const textarea = textareaRef.current
      if (textarea) {
        textarea.style.height = 'auto'
        textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`
      }
    }, [localValue])

    const addFiles = async (files: FileList | null) => {
      if (!files || files.length === 0) return

      const fileArray = Array.from(files)
      const validationConfig = getDefaultFileValidationConfig()

      // Validate files
      const validationError = validateFiles(fileArray, attachments, validationConfig)
      if (validationError) {
        setUploadErrors((prev) => {
          const next = new Map(prev)
          fileArray.forEach((f) => {
            next.set(f.name, validationError.message)
          })
          return next
        })
        return
      }

      // Clear any previous errors for these files
      setUploadErrors((prev) => {
        const next = new Map(prev)
        fileArray.forEach((f) => next.delete(f.name))
        return next
      })

      try {
        // Get transport config for presigned URL endpoint
        const transportConfig = runtime.config.transport
        if (transportConfig.mode !== 'proxy') {
          // Direct transport doesn't support file uploads
          throw new Error('File uploads are only supported with proxy transport')
        }

        const uploadConfig = {
          presignedUrlEndpoint: `${transportConfig.baseUrl}/files/upload/presigned`,
          getAuthHeaders: () => {
            const headers: Record<string, string> = {}
            if (transportConfig.headers) {
              return transportConfig.headers
            }
            if (transportConfig.apiKey) {
              headers['Authorization'] = `Bearer ${transportConfig.apiKey}`
            }
            return headers
          }
        }

        // Process and upload files
        const processedAttachments = await processFiles(fileArray, uploadConfig, {
          chatId: activeChatId,
          userId: runtime.config.user?.id,
          onProgress: (progress) => {
            setUploadProgress((prev) => {
              const next = new Map(prev)
              next.set(progress.fileId, progress)
              return next
            })
          }
        })

        // Add processed attachments to composer
        addComposerAttachments(chatKey, processedAttachments)

        // Clear progress after a delay
        setTimeout(() => {
          setUploadProgress((prev) => {
            const next = new Map(prev)
            processedAttachments.forEach((att) => next.delete(att.id))
            return next
          })
        }, 2000)
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to upload files'
        setUploadErrors((prev) => {
          const next = new Map(prev)
          fileArray.forEach((f) => {
            next.set(f.name, errorMessage)
          })
          return next
        })
      }
    }

    const handlePickFiles = () => {
      fileInputRef.current?.click()
    }

    const tryAddLinkAttachmentFromText = (text: string): boolean => {
      const url = normalizeUrl(text)
      if (!url) return false

      // don't add identical URL twice
      if (attachments.some((a) => a.type === AgentAttachmentType.Link && a.url === url)) return true

      const name = getUrlName(url)

      const next: AgentAttachment = {
        id: nanoid(),
        url,
        name,
        type: AgentAttachmentType.Link
      }

      addComposerAttachments(chatKey, [next])
      return true
    }

    const handleFileInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
      await addFiles(e.target.files)
      e.target.value = ''
    }

    const removeAttachment = (id: string) => {
      const att = attachments.find((a) => a.id === id)
      if (att && isPageContext(att)) {
        setPageContextDismissed(chatKey, true)
      }
      removeComposerAttachment(chatKey, id)
    }

    const tryAddWebSnippetFromSelection = (): boolean => {
      // Safe-guard for non-browser environments.
      if (typeof window === 'undefined' || typeof document === 'undefined') return false
      const captured = captureTextSelectionTarget()
      const selectedText = captured?.selectedText ?? window.getSelection?.()?.toString()?.trim() ?? ''
      if (!selectedText) return false

      const url = window.location?.href
      const truncatedText = selectedText.slice(0, 20)
      const title = selectedText.length > 20 ? truncatedText + '...' : selectedText

      const next: AgentAttachment = createContextAttachment({
        kind: 'webSnippet',
        url,
        title,
        selectedText,
        source: { app: 'browser', url },
        ...(captured?.target ? { target: captured.target } : {})
      })

      addComposerAttachments(chatKey, [next])
      return true
    }

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      const content = localValue.trim()
      if (!content) return
      setComposerError(chatKey, undefined)
      onSubmit(content)
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        handleSubmit(e as any)
      }
    }

    const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (composerError) setComposerError(chatKey, undefined)
      setComposerValue(chatKey, event.target.value)
    }

    const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
      // Allow users to force normal paste with Shift+Paste.
      if ((e.nativeEvent as unknown as { shiftKey?: boolean }).shiftKey) return
      const text = e.clipboardData?.getData('text/plain') ?? ''
      if (!text) return
      const didAdd = tryAddLinkAttachmentFromText(text)
      if (didAdd) {
        e.preventDefault()
      }
    }

    const content = localValue.trim()
    const canSend = Boolean(content)

    return (
      <>
        <form onSubmit={handleSubmit} className='mp-agent-composer-input-wrapper' id='composer-form'>
          <input ref={fileInputRef} type='file' multiple style={{ display: 'none' }} onChange={handleFileInputChange} />
          <ComposerAttachments
            attachments={attachments}
            onRemove={removeAttachment}
            uploadProgress={uploadProgress}
            uploadErrors={uploadErrors}
          />
          <textarea
            rows={2}
            name='message'
            ref={textareaRef}
            value={localValue}
            autoFocus={autoFocus}
            className='mp-agent-composer-textarea'
            placeholder={activeChatId ? t.followUp : t.newChat}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
          />
        </form>
        <div className='mp-agent-composer-actions'>
          <div className='mp-agent-composer-actions-left'>
            {runtime.config.features?.modelSwitching && <ModelSelector />}
            {runtime.config.features?.toolConfiguration && <ToolConfigDropdown />}
            {runtime.config.features?.fileAttachments && (
              <Tooltip content={t.attachFiles}>
                <button
                  type='button'
                  className='mp-agent-composer-button mp-agent-composer-tool-button'
                  onClick={handlePickFiles}
                  aria-label={t.attachFiles}
                >
                  <Paperclip size={16} />
                </button>
              </Tooltip>
            )}
            {runtime.config.features?.selectionAttachments && (
              <Tooltip content={t.attachSelectionToContext}>
                <button
                  type='button'
                  className='mp-agent-composer-button mp-agent-composer-tool-button'
                  onClick={() => {
                    tryAddWebSnippetFromSelection()
                  }}
                  aria-label={t.attachSelectionToContext}
                >
                  <ScanText size={16} />
                </button>
              </Tooltip>
            )}
            {pageContextEnabled && !hasPageContext && (
              <Tooltip content={attachPageContextLabel}>
                <button
                  type='button'
                  className='mp-agent-composer-button mp-agent-composer-tool-button'
                  onClick={() => {
                    setPageContextDismissed(chatKey, false)
                    const page = buildPageContextAttachment()
                    if (page) addComposerAttachments(chatKey, [page])
                  }}
                  aria-label={attachPageContextLabel}
                >
                  {pageContextIcon}
                </button>
              </Tooltip>
            )}
            {ComposerAttachmentActions && (
              <ComposerAttachmentActions
                chatKey={chatKey}
                activeChatId={activeChatId}
                attachments={attachments}
                addAttachments={(next) => addComposerAttachments(chatKey, next)}
                removeAttachment={removeAttachment}
              />
            )}
          </div>
          <div className='mp-agent-composer-actions-right'>
            {status === UiChatStatus.Streaming && !canSend ? (
              <Tooltip content={t.stopStreaming}>
                <button
                  type='button'
                  onClick={onStop}
                  aria-label={t.stopStreaming}
                  className='mp-agent-composer-button mp-agent-composer-send-button'
                >
                  <Square size={12} />
                </button>
              </Tooltip>
            ) : (
              <Tooltip content={canSend ? t.sendMessage : t.typeMessageToSend}>
                <button
                  type='submit'
                  form='composer-form'
                  disabled={!canSend}
                  aria-label={t.sendMessage}
                  className='mp-agent-composer-button mp-agent-composer-send-button'
                >
                  <ArrowUp size={16} />
                </button>
              </Tooltip>
            )}
          </div>
        </div>
      </>
    )
  }
)

export default ComposerInputRow
